import { Box, Center, Heading, Spinner, Text, VStack } from '@chakra-ui/react'
import { AppLayout } from '@src/components/sco/AppLayout'
import { PitchDeckChatPanel } from '@src/components/sco/PitchDeckAuditor/chat/PitchDeckChatPanel'
import { usePitchDeckView } from '@src/components/sco/PitchDeckAuditor/hooks/useScoPitchDeck'
import { PitchDeckMainPanel } from '@src/components/sco/PitchDeckAuditor/main/PitchDeckMainPanel'
import { DeckNav } from '@src/components/sco/PitchDeckAuditor/nav/DeckNav'
import { PitchDeckProcessing } from '@src/components/sco/PitchDeckAuditor/processing/PitchDeckProcessing'
import { PitchDeckNeedsUpload } from '@src/components/sco/PitchDeckAuditor/start/PitchDeckNeedsUpload'
import { useQueryState } from 'nuqs'
import React from 'react'

/**
 * Pitch Deck Auditor embedded inside another workspace (Request manage, Investor Matching, etc.).
 * Does not own site chrome — parent layout provides nav. Slide selection uses `?slideId=`.
 */
export function PitchDeckEmbedded({ pitchDeckId }: { pitchDeckId: string }) {
  const [slideId, setSlideId] = useQueryState('slideId')

  return (
    <PitchDeckEmbeddedView
      activeSlideId={slideId}
      pitchDeckId={pitchDeckId}
      onSelectSlide={id => void setSlideId(id)}
    />
  )
}

export function PitchDeckEmbeddedView({
  pitchDeckId,
  activeSlideId,
  onSelectSlide,
}: {
  pitchDeckId: string
  activeSlideId: string | null
  onSelectSlide: (slideId: string | null) => void
}) {
  const { deck, raw, loading, error, refetch } = usePitchDeckView(pitchDeckId)

  if (loading && !deck) {
    return (
      <Center padding={10} width={'full'}>
        <Spinner colorPalette={'orange'} size={'xl'} />
      </Center>
    )
  }

  if (error || !deck) {
    return (
      <VStack align={'stretch'} gap={4} width={'full'}>
        <VStack gap={2} padding={10}>
          <Heading size={'md'}>Deck not found</Heading>
          <Text color={'fg.muted'} fontSize={'sm'}>
            This pitch deck doesn&rsquo;t exist or you don&rsquo;t have access to it.
          </Text>
        </VStack>
      </VStack>
    )
  }

  // Empty shell (no PDF yet) — upload only; skip processing/auditor chrome.
  if (!deck.hasStoredFile) {
    return (
      <VStack align={'stretch'} gap={0} paddingX={4} width={'full'}>
        <PitchDeckNeedsUpload
          pitchDeckId={deck.pitchDeckId}
          title={raw?.title}
          onUploaded={() => void refetch()}
        />
      </VStack>
    )
  }

  if (!deck.isAudited) {
    return (
      <VStack align={'stretch'} gap={0} width={'full'}>
        <PitchDeckProcessing deck={deck} />
      </VStack>
    )
  }

  return (
    <VStack align={'stretch'} gap={0} height={'full'} width={'full'}>
      <Box flex={1} minHeight={0} width={'full'}>
        <AppLayout
          containerProps={{ height: 'full', maxHeight: 'full' }}
          id={'pitch-deck-embedded'}
          panelProps={{
            paddingX: 0,
            paddingTop: 0,
            paddingBottom: 0,
            marginX: 0,
            marginBottom: 0,
            boxShadow: 'none',
            gap: 0,
          }}
          panels={[
            {
              id: 'pitch-deck-embedded-nav',
              defaultWidth: 300,
              minWidth: 260,
              maxWidth: 380,
              isResizable: true,
              isPanelOpen: true,
              resizeHandlePosition: 'right',
              useDrawerBreakpoints: { base: true, lg: false },
              containerProps: {
                paddingTop: 4,
              },
              children: (
                <VStack align={'stretch'} gap={0} height={'full'} justify={'stretch'} paddingTop={2}>
                  <DeckNav
                    activeSlideId={activeSlideId}
                    deck={deck}
                    onReplaced={() => void refetch()}
                    onSelectSlide={onSelectSlide}
                  />
                </VStack>
              ),
            },
            {
              id: 'pitch-deck-embedded-main',
              resetScrollOnPathChange: true,
              children: (
                <PitchDeckMainPanel activeSlideId={activeSlideId} deck={deck} onSelectSlide={onSelectSlide} />
              ),
            },
            {
              id: 'pitch-deck-embedded-aside',
              isPanelOpen: true,
              isResizable: true,
              resizeHandlePosition: 'left',
              scrollable: false,
              defaultWidth: 420,
              minWidth: 340,
              maxWidth: 560,
              useDrawerBreakpoints: { base: true, xl: false },
              children: <PitchDeckChatPanel activeSlideId={activeSlideId} deck={deck} />,
            },
          ]}
        />
      </Box>
    </VStack>
  )
}
